const BigNumber = require('bignumber.js');
const ItemType = require('@/lib/Items/ItemType');

/**
 * @param {OrderLine|CartLine} line
 *
 * @return {number}
 */
const countPeopleOnLine = (line) => {
    /**
     * @type {AbstractItem|Ticket}
     */
    const item = line.getItem();

    if (!item || item.itemType !== ItemType.TICKET) {
        return 0;
    }

    return line.getQuantity() * item.people;
};

/**
 * @param {CartLine[]|OrderLine[]} lines
 *
 * @return {number}
 */
const countPeopleOnLines = (lines) => {
    return lines.reduce((prev, line) => {
        return prev + countPeopleOnLine(line);
    }, 0);
};

/**
 * @param {CartLine[]|OrderLine[]} lines
 *
 * @return {number}
 */
const countTicketsOnLines = (lines) => {
    return lines.reduce((prev, line) => {
        if (line.getItem().itemType === ItemType.TICKET) {
            return prev + line.getQuantity();
        }

        return prev;
    }, 0);
};

/**
 * @return {{tickets: {total: number, unredeemed: number, redeemed: number}, people: {total: number, unredeemed: number, redeemed: number}}}
 */
const createEmptyRedemptionCounts = () => {
    return {
        tickets: {
            total: 0,
            redeemed: 0,
            unredeemed: 0,
        },
        people: {
            total: 0,
            redeemed: 0,
            unredeemed: 0,
        },
    };
};

/**
 * Returns a breakdown of the number of (un)redeemed tickets and people on the given line.
 *
 * @param {CartLine|OrderLine} line
 *
 * @return {{tickets: {total: number, unredeemed: number, redeemed: number}, people: {total: number, unredeemed: number, redeemed: number}}}
 */
const countRedemptionsOnLine = (line) => {
    const counts = createEmptyRedemptionCounts();

    /**
     * @type {Ticket}
     */
    const ticket = line.getItem();
    if (ticket.itemType !== ItemType.TICKET) {
        return counts;
    }

    if (line.hasIndividualTicketInstances()) {
        // Count the number of instances that are unredeemed.
        line.getItemInstances().forEach(
            /**
             * @param {ItemInstance} instance
             */
            (instance) => {
                counts.tickets.total += 1;
                if (instance.netRedemptions > 0) {
                    counts.tickets.redeemed += 1;
                    counts.people.redeemed += ticket.people;
                } else {
                    counts.tickets.unredeemed += 1;
                    counts.people.unredeemed += ticket.people;
                }
            }
        );
    } else {
        counts.tickets.total = line.getQuantity();
        counts.tickets.redeemed = line.redeemed;
        counts.tickets.unredeemed = line.getQuantity() - line.redeemed;

        counts.people.total = counts.tickets.total * ticket.people;
        counts.people.redeemed = counts.tickets.redeemed * ticket.people;
        counts.people.unredeemed = counts.tickets.unredeemed * ticket.people;
    }

    return counts;
};

/**
 * @param {CartLine[]|OrderLine[]} lines
 *
 * @return {{tickets: {total: number, unredeemed: number, redeemed: number}, people: {total: number, unredeemed: number, redeemed: number}}}
 */
const countRedemptionsOnLines = (lines) => {
    const counts = createEmptyRedemptionCounts();

    lines.forEach((line) => {
        const lineCounts = countRedemptionsOnLine(line);
        counts.tickets.total += lineCounts.tickets.total;
        counts.tickets.redeemed += lineCounts.tickets.redeemed;
        counts.tickets.unredeemed += lineCounts.tickets.unredeemed;

        counts.people.total += lineCounts.people.total;
        counts.people.redeemed += lineCounts.people.redeemed;
        counts.people.unredeemed += lineCounts.people.unredeemed;
    });

    return counts;
};

/**
 * Receives an array of lines and returns an object with the total value for each different itemID.
 *
 * @param {CartLine[]|OrderLine[]} lines
 * @param {function(line:CartLine)} getLineTotalMethod A function which will receive each line and should return the
 *                                                     line's total for the purposes of this calculation.
 *
 * @return {Object<number, number>}
 */
const sumTotalOfLinesByItemID = (lines, getLineTotalMethod) => {
    /**
     * @type {Object<number, BigNumber>}
     */
    const totalsByItemID = {};

    lines.forEach((line) => {
        const itemID = line.getItemID();
        if (!totalsByItemID.hasOwnProperty(itemID)) {
            totalsByItemID[itemID] = new BigNumber(0);
        }
        totalsByItemID[itemID] = totalsByItemID[itemID].plus(getLineTotalMethod(line));
    });

    const totalsByItemIDAsNumbers = {};
    Object.keys(totalsByItemID).forEach((itemID) => {
        totalsByItemIDAsNumbers[itemID] = totalsByItemID[itemID].toNumber();
    });

    return totalsByItemIDAsNumbers;
};

module.exports = {
    countPeopleOnLine,
    countPeopleOnLines,
    countTicketsOnLines,
    countRedemptionsOnLine,
    countRedemptionsOnLines,
    sumTotalOfLinesByItemID,
};
